import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import {
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
} from "../Helper/formbtnhelper.js";
import NewEmail from "../Inputs/NewEmail";
import NewPhone from "../Inputs/NewPhone";
import NewSelect from "../Inputs/NewSelect";
import NewText from "../Inputs/NewText";
import NewTitle from "../Inputs/NewTitle";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper.js";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";

// Slide 7: Owner Information
const OwnerInformation = ({ setSlideStatus, slideState, formStateLoaded }) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const {
    slide3,
    slide5,
    setSlide5,
    setIsLoading,
    requestStatus,
    names,
    phoneNumbers,
    emails,
    titles,
    companies,
    predictiveTextFetched,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
  } = useContext(FormContext);

  const { client, clientId, projectId } = useContext(DashboardContext);

  // On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide5);
  }, []);

  // Update Slide State
  const slide5Mutation = useSetSlide(
    clientId,
    projectId,
    5,
    setIsLoading,
    setSlideStatus,
  );
  // GET INVALID INPUTS
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "5",
    );
  }, [invalidInputsData.isFetched]);

  useEffect(() => {
    if (
      slide5.oName === client.name &&
      slide5.oAdd === "" &&
      slide5.oZip === "" &&
      slide5.oPhone === "" &&
      slide5.oFax === "" &&
      slide5.oTaxId === "" &&
      slide5.oCity === "" &&
      slide5.oState === ""
    ) {
      let newState = {
        ...slide5,
        oName: client.name,
        oAdd: client.address ? client.address : "",
        oState: client.state ? client.state : "",
        oCity: client.city ? client.city : "",
        oZip: client.zip ? client.zip : "",
        oPhone: client.phone ? client.phone : "",
        oFax: client.fax ? client.fax : "",
        oTaxId: client.taxId ? client.taxId : "",
        oContactName: client.shortName === "Greystone" ? "Joy Broddle" : "",
        oContactTitle:
          client.shortName === "Greystone" ? "Division President" : "",
        oContactEmail:
          client.shortName === "Greystone" ? "joy.broddle@lennar.com" : "",
        oContactPhone: client.shortName === "Greystone" ? "702-736-9100" : "",
        oAuthorizedContactName:
          client.shortName === "Greystone" ? "Courtney Breen" : "",
        oAuthorizedContactTitle:
          client.shortName === "Greystone"
            ? "Division Environmental Manager"
            : "",
      };

      if (client.shortName === "Greystone") {
        newState = {
          ...newState,
          oContactName: "Joy Broddle",
          oContactTitle: "Division President",
          oContactEmail: "joy.broddle@lennar.com",
          oContactPhone: "702-736-9100",
          oAuthorizedContactName: "Courtney Breen",
          oAuthorizedContactTitle: "Division Environmental Manager",
        };
      }
      setSlide5(newState);

      slide5Mutation.mutate(newState);
    }
  }, [slide5]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <p className="important-text">
            Please provide the following information for the owner of this
            project. <br /> All fields should match exactly as registered when
            this entity was formed.
          </p>
          <ResponsiveRow>
            <NewText
              idProp="oName"
              label="Owning Company Name"
              type="company"
              state={slide5}
              setState={setSlide5}
              val={slide5.oName}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? companies : []}
              req={slide5.oName === ""}
              disabledInput={disabledInputCheck("oName", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oName", disabledInputs) === false
                  ? getInputMessage("oName", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oName", invalidInputsData, "5")}
            />
            <NewText
              idProp="oTaxId"
              label="Tax ID # (TIN)"
              type="taxId"
              state={slide5}
              setState={setSlide5}
              val={slide5.oTaxId}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              req={slide5.oTaxId === ""}
              disabledInput={disabledInputCheck("oTaxId", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oTaxId", disabledInputs) === false
                  ? getInputMessage("oTaxId", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oTaxId", invalidInputsData, "5")}
            />
            <NewPhone
              idProp="oPhone"
              label="Office Phone #"
              state={slide5}
              setState={setSlide5}
              val={slide5.oPhone}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              req={!isValidPhoneNumber(slide5.oPhone, false)}
              disabledInput={disabledInputCheck("oPhone", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oPhone", disabledInputs) === false
                  ? getInputMessage("oPhone", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oPhone", invalidInputsData, "5")}
              type="phone"
            />
            <NewText
              idProp="oFax"
              label="Fax Number"
              state={slide5}
              setState={setSlide5}
              val={slide5.oFax}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              type="fax"
              req={slide5.oFax === ""}
              disabledInput={disabledInputCheck("oFax", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oFax", disabledInputs) === false
                  ? getInputMessage("oFax", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oFax", invalidInputsData, "5")}
            />
          </ResponsiveRow>
          <ResponsiveRow>
            <NewText
              idProp="oAdd"
              label="Office Address"
              type={"address"}
              state={slide5}
              setState={setSlide5}
              val={slide5.oAdd}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              req={slide5.oAdd === ""}
              disabledInput={disabledInputCheck("oAdd", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oAdd", disabledInputs) === false
                  ? getInputMessage("oAdd", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oAdd", invalidInputsData, "5")}
            />
            <NewText
              idProp="oCity"
              label="City"
              state={slide5}
              setState={setSlide5}
              val={slide5.oCity}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              req={slide5.oCity === ""}
              disabledInput={disabledInputCheck("oCity", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oCity", disabledInputs) === false
                  ? getInputMessage("oCity", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oCity", invalidInputsData, "5")}
              type={"city"}
            />
            <NewSelect
              idProp="oState"
              label="State"
              state={slide5}
              setState={setSlide5}
              val={slide5.oState}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              req={slide5.oState === ""}
              disabledInput={disabledInputCheck("oState", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oState", disabledInputs) === false
                  ? getInputMessage("oState", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oState", invalidInputsData, "5")}
              type="state"
            />
            <NewText
              idProp="oZip"
              label="Zip"
              state={slide5}
              setState={setSlide5}
              val={slide5.oZip}
              debouncedSave={slide5Mutation}
              setIsLoading={setIsLoading}
              req={slide5.oZip === ""}
              disabledInput={disabledInputCheck("oZip", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("oZip", disabledInputs) === false
                  ? getInputMessage("oZip", invalidInputsData, "5")
                  : ""
              }
              invalidData={getInvalidData("oZip", invalidInputsData, "5")}
              type={"zipCode"}
            />
          </ResponsiveRow>
          <>
            <h2 className="question-section">Contact Information</h2>
            <p className="important-text">
              {
                "Please provide the contact information of the owner's primary contact"
              }
            </p>
            <ResponsiveRow>
              <NewText
                idProp="oContactName"
                label="First & Last Name"
                state={slide5}
                setState={setSlide5}
                val={slide5.oContactName}
                debouncedSave={slide5Mutation}
                setIsLoading={setIsLoading}
                autoData={predictiveTextFetched ? names : []}
                type="name"
                req={!isValidName(slide5.oContactName, false)}
                disabledInput={disabledInputCheck(
                  "oContactName",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck("oContactName", disabledInputs) === false
                    ? getInputMessage("oContactName", invalidInputsData, "5")
                    : ""
                }
                invalidData={getInvalidData(
                  "oContactName",
                  invalidInputsData,
                  "5",
                )}
              />
              <NewTitle
                idProp="oContactTitle"
                label="Title"
                state={slide5}
                setState={setSlide5}
                val={slide5.oContactTitle}
                debouncedSave={slide5Mutation}
                setIsLoading={setIsLoading}
                autoData={predictiveTextFetched ? titles : []}
                req={slide5.oContactTitle === ""}
                disabledInput={disabledInputCheck(
                  "oContactTitle",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck("oContactTitle", disabledInputs) === false
                    ? getInputMessage("oContactTitle", invalidInputsData, "5")
                    : ""
                }
                invalidData={getInvalidData(
                  "oContactTitle",
                  invalidInputsData,
                  "5",
                )}
              />
              <NewPhone
                idProp="oContactPhone"
                label="Phone #"
                state={slide5}
                setState={setSlide5}
                val={slide5.oContactPhone}
                debouncedSave={slide5Mutation}
                setIsLoading={setIsLoading}
                autoData={predictiveTextFetched ? phoneNumbers : []}
                req={!isValidPhoneNumber(slide5.oContactPhone, false)}
                disabledInput={disabledInputCheck(
                  "oContactPhone",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck("oContactPhone", disabledInputs) === false
                    ? getInputMessage("oContactPhone", invalidInputsData, "5")
                    : ""
                }
                invalidData={getInvalidData(
                  "oContactPhone",
                  invalidInputsData,
                  "5",
                )}
              />
              <NewEmail
                idProp="oContactEmail"
                label=" Email Address"
                state={slide5}
                setState={setSlide5}
                val={slide5.oContactEmail}
                debouncedSave={slide5Mutation}
                setIsLoading={setIsLoading}
                autoData={predictiveTextFetched ? emails : []}
                req={!isValidEmail(slide5.oContactEmail, false)}
                disabledInput={disabledInputCheck(
                  "oContactEmail",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck("oContactEmail", disabledInputs) === false
                    ? getInputMessage("oContactEmail", invalidInputsData, "5")
                    : ""
                }
                invalidData={getInvalidData(
                  "oContactEmail",
                  invalidInputsData,
                  "5",
                )}
              />
            </ResponsiveRow>
            {slide3.permittee === "owner" && (
              <>
                <h2 className="question-section">
                  Authorized Signer Information
                </h2>
                <p className="important-text">
                  {
                    "Please provide the contact information of the individual who will be the authorized signer for permits & SWPPPs"
                  }
                </p>
                <ResponsiveRow type={"center"}>
                  <NewText
                    idProp="oAuthorizedContactName"
                    label="First & Last Name"
                    state={slide5}
                    setState={setSlide5}
                    val={slide5.oAuthorizedContactName}
                    debouncedSave={slide5Mutation}
                    setIsLoading={setIsLoading}
                    autoData={predictiveTextFetched ? names : []}
                    type="name"
                    req={!isValidName(slide5.oAuthorizedContactName, false)}
                    disabledInput={disabledInputCheck(
                      "oAuthorizedContactName",
                      disabledInputs,
                    )}
                    disabledInputMessage={
                      disabledInputCheck(
                        "oAuthorizedContactName",
                        disabledInputs,
                      ) === false
                        ? getInputMessage(
                            "oAuthorizedContactName",
                            invalidInputsData,
                            "5",
                          )
                        : ""
                    }
                    invalidData={getInvalidData(
                      "oAuthorizedContactName",
                      invalidInputsData,
                      "5",
                    )}
                  />
                  <NewTitle
                    idProp="oAuthorizedContactTitle"
                    label="Title"
                    state={slide5}
                    setState={setSlide5}
                    val={slide5.oAuthorizedContactTitle}
                    debouncedSave={slide5Mutation}
                    setIsLoading={setIsLoading}
                    autoData={predictiveTextFetched ? titles : []}
                    type="title"
                    req={slide5.oAuthorizedContactTitle === ""}
                    disabledInput={disabledInputCheck(
                      "oAuthorizedContactTitle",
                      disabledInputs,
                    )}
                    disabledInputMessage={
                      disabledInputCheck(
                        "oAuthorizedContactTitle",
                        disabledInputs,
                      ) === false
                        ? getInputMessage(
                            "oAuthorizedContactTitle",
                            invalidInputsData,
                            "5",
                          )
                        : ""
                    }
                    invalidData={getInvalidData(
                      "oAuthorizedContactTitle",
                      invalidInputsData,
                      "5",
                    )}
                  />
                </ResponsiveRow>
              </>
            )}
          </>
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default OwnerInformation;
